/**
 * Arranging a whole squad on the pitch.
 *
 * The positions sheet shows eleven players in a shape. This shows everyone,
 * standing where they play, in the order the depth chart ranks them — the page a
 * coach actually wants when somebody asks "what have you got?".
 *
 * Pure, so the arrangement can be tested without a canvas.
 */

import { findFormation, unitOf } from '@shared/formations';

export interface SheetPlayer {
  id: string;
  name: string;
  shirtNumber?: number | null;
  primaryPositionKey?: string | null;
  secondaryPositionKey?: string | null;
  roleKey?: string | null;
}

export interface SheetSlot {
  positionKey: string;
  x: number;
  y: number;
  unit: 'gk' | 'defence' | 'midfield' | 'attack';
  players: SheetPlayer[];
}

export interface BuiltSquadSheet {
  slots: SheetSlot[];
  /** Everyone the shape has no place for. Listed, never dropped. */
  unplaced: SheetPlayer[];
}

/** Depth chart order, when one exists: slot position → ranked player ids. */
export type DepthOrder = Map<string, string[]>;

/** Position key → the positions a player there can also fill. */
export type CoverMap = Map<string, string[]>;

/**
 * @param ranked ids ranked per position by the depth chart; when a position is
 *               absent from it, players fall back to their own primary position
 */
export function buildSquadSheet(
  formationKey: string,
  players: readonly SheetPlayer[],
  ranked: DepthOrder = new Map(),
  /**
   * Which positions each position can also fill, from the club's own
   * configuration. A centre back plays left or right of centre; a club that
   * trains its full backs as wing backs says so there, not here.
   */
  covers: CoverMap = new Map(),
): BuiltSquadSheet {
  const formation = findFormation(formationKey);
  if (!formation) return { slots: [], unplaced: [...players] };

  const byId = new Map(players.map((p) => [p.id, p]));

  // Everyone who plays a position, in the coach's order. The depth chart is that
  // order and always wins; only a position missing from it falls back to what
  // players themselves say they play.
  const forPosition = (positionKey: string): SheetPlayer[] => {
    const fromChart = (ranked.get(positionKey) ?? [])
      .map((id) => byId.get(id))
      .filter((p): p is SheetPlayer => Boolean(p));
    return fromChart.length
      ? fromChart
      : players.filter((p) => p.primaryPositionKey === positionKey);
  };

  const slots: SheetSlot[] = formation.slots.map((slot) => ({
    positionKey: slot.positionKey,
    x: slot.x,
    y: slot.y,
    unit: unitOf(slot.positionKey),
    players: [],
  }));

  /**
   * Which slots can hold a player of this position.
   *
   * A slot of the same name first. Only if the shape has none does the cover
   * map apply — so a centre back in a 4-2-3-1, which has LCB and RCB but no CB,
   * is hosted by both, and a squad of four is shared between them rather than
   * piled onto whichever slot the formation happens to list first.
   */
  const hostsFor = (positionKey: string): number[] => {
    const own = slots
      .map((s, i) => (s.positionKey === positionKey ? i : -1))
      .filter((i) => i >= 0);
    if (own.length > 0) return own;

    const canCover = covers.get(positionKey) ?? [];
    return slots
      .map((s, i) => (canCover.includes(s.positionKey) ? i : -1))
      .filter((i) => i >= 0);
  };

  const placed = new Set<string>();
  const positions = [
    ...new Set([
      ...players.map((p) => p.primaryPositionKey).filter((k): k is string => Boolean(k)),
      ...ranked.keys(),
    ]),
  ];

  for (const positionKey of positions) {
    const hosts = hostsFor(positionKey);
    if (hosts.length === 0) continue;

    const queue = forPosition(positionKey).filter((p) => !placed.has(p.id));
    queue.forEach((player, i) => {
      // Round robin, so first choice and second choice stand on opposite sides
      // rather than one slot taking everybody.
      slots[hosts[i % hosts.length]!]!.players.push(player);
      placed.add(player.id);
    });
  }

  // A player whose secondary position is in the shape is better shown there than
  // in a list of leftovers — but only if nobody has claimed him already.
  for (const p of players) {
    if (placed.has(p.id) || !p.secondaryPositionKey) continue;
    const hosts = hostsFor(p.secondaryPositionKey);
    if (hosts.length === 0) continue;
    slots[hosts[0]!]!.players.push(p);
    placed.add(p.id);
  }

  return {
    slots,
    unplaced: players.filter((p) => !placed.has(p.id)),
  };
}

/**
 * The coach's own placements, laid over the arrangement the app worked out.
 *
 * It is not enough to change a player's position: the depth chart wins over a
 * player's own position inside `buildSquadSheet`, so a player moved to the wing
 * while the chart still lists him at left back would be drawn in both places.
 * A placement therefore takes him out of every ranked list and puts him in the
 * one he was placed in, which is what "I decided" has to mean.
 *
 * The chart itself is untouched — this returns new values and writes nothing.
 * The sheet and the depth chart are allowed to disagree; they answer different
 * questions.
 */
export function applyPlacements(
  players: readonly SheetPlayer[],
  ranked: DepthOrder,
  placements: Readonly<Record<string, string>>,
): { players: SheetPlayer[]; ranked: DepthOrder } {
  const placed = new Map(Object.entries(placements));
  if (placed.size === 0) return { players: [...players], ranked };

  const nextRanked: DepthOrder = new Map();
  for (const [positionKey, ids] of ranked) {
    nextRanked.set(
      positionKey,
      ids.filter((id) => !placed.has(id)),
    );
  }
  for (const [playerId, positionKey] of placed) {
    // Appended, not inserted: a placement says where, and the order within a
    // position is still the chart's business.
    nextRanked.set(positionKey, [...(nextRanked.get(positionKey) ?? []), playerId]);
  }

  return {
    players: players.map((p) =>
      placed.has(p.id) ? { ...p, primaryPositionKey: placed.get(p.id) } : p,
    ),
    ranked: nextRanked,
  };
}
